import React, { useState } from 'react';

const CpcTrafficClientsQuickActionModal = ({ record, onClose, onSave, currentUser }) => {
  const [selectedAction, setSelectedAction] = useState('');
  const [description, setDescription] = useState('');
  const [details, setDetails] = useState('');
  const [loading, setLoading] = useState(false);

  const actionOptions = [
    {
      value: 'status_changed',
      label: 'Cambiar Estado',
      icon: '🔄',
      description: 'Cambiar el estado del registro'
    },
    {
      value: 'comment_added',
      label: 'Agregar Comentario',
      icon: '💬',
      description: 'Agregar un comentario o nota'
    },
    {
      value: 'action_taken',
      label: 'Acción Realizada',
      icon: '⚡',
      description: 'Registrar una acción específica tomada'
    },
    {
      value: 'issue_resolved',
      label: 'Problema Resuelto',
      icon: '✅',
      description: 'Marcar el problema como resuelto'
    },
    {
      value: 'escalated',
      label: 'Escalar Problema',
      icon: '⬆️',
      description: 'Escalar a un nivel superior'
    },
    {
      value: 'follow_up',
      label: 'Programar Seguimiento',
      icon: '📞',
      description: 'Programar un seguimiento futuro'
    }
  ];

  const statusOptions = [
    { value: 'pendiente', label: 'Pendiente' },
    { value: 'en_proceso', label: 'En Proceso' },
    { value: 'completado', label: 'Completado' },
    { value: 'cancelado', label: 'Cancelado' }
  ];

  const [newStatus, setNewStatus] = useState(record?.status || 'pendiente');

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedAction) {
      alert('Por favor, selecciona una acción');
      return;
    }

    if (!description.trim()) {
      alert('Por favor, proporciona una descripción');
      return;
    }

    setLoading(true);
    
    try {
      const actionData = {
        action_type: selectedAction,
        description: description.trim(),
        details: details.trim() || null,
        user_name: currentUser?.name || currentUser?.email || 'Sistema'
      };

      // Si es cambio de estado, agregar el nuevo estado
      if (selectedAction === 'status_changed') {
        actionData.new_status = newStatus;
        actionData.description = `Estado cambiado de "${record.status}" a "${newStatus}": ${description.trim()}`;
      }

      await onSave(record.id, actionData);
    } catch (error) {
      console.error('Error al guardar acción:', error);
      alert(`Error al guardar: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const getActionDetails = () => {
    const action = actionOptions.find(opt => opt.value === selectedAction);
    return action ? action.description : '';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-pink-600 text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-bold">
              ⚡ Acción Rápida - CPC y Tráfico
            </h2>
            <button
              onClick={onClose}
              className="text-white hover:text-gray-200 text-2xl font-bold"
            >
              ✕
            </button>
          </div>
          <p className="text-purple-100 mt-2">
            Cliente: {record?.nombre_cliente}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Selección de Acción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Tipo de Acción *
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {actionOptions.map((option) => (
                <div
                  key={option.value}
                  className={`relative cursor-pointer rounded-lg border p-4 hover:bg-gray-50 transition-colors ${
                    selectedAction === option.value
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300'
                  }`}
                  onClick={() => setSelectedAction(option.value)}
                >
                  <div className="flex items-center">
                    <input
                      type="radio"
                      name="action"
                      value={option.value}
                      checked={selectedAction === option.value}
                      onChange={() => setSelectedAction(option.value)}
                      className="sr-only"
                    />
                    <div className="text-2xl mr-3">{option.icon}</div>
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {option.label}
                      </div>
                      <div className="text-xs text-gray-500">
                        {option.description}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Cambio de Estado (solo si se selecciona status_changed) */}
          {selectedAction === 'status_changed' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Nuevo Estado *
              </label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
              >
                {statusOptions.map((status) => (
                  <option key={status.value} value={status.value}>
                    {status.label}
                  </option>
                ))}
              </select>
              <p className="text-sm text-gray-500 mt-1">
                Estado actual: <span className="font-medium">{record?.status}</span>
              </p>
            </div>
          )}

          {/* Descripción */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Descripción *
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
              rows="3"
              placeholder={
                selectedAction === 'status_changed'
                  ? 'Explica el motivo del cambio de estado...'
                  : selectedAction === 'comment_added'
                  ? 'Escribe tu comentario...'
                  : selectedAction === 'action_taken'
                  ? 'Describe la acción realizada...'
                  : selectedAction === 'issue_resolved'
                  ? 'Explica cómo se resolvió el problema...'
                  : selectedAction === 'escalated'
                  ? 'Motivo de la escalación...'
                  : selectedAction === 'follow_up'
                  ? 'Detalles del seguimiento programado...'
                  : 'Describe la acción realizada...'
              }
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
            {selectedAction && (
              <p className="text-sm text-gray-500 mt-1">
                {getActionDetails()}
              </p>
            )}
          </div>

          {/* Detalles Adicionales */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Detalles Adicionales (Opcional)
            </label>
            <textarea
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              rows="2"
              placeholder="Información adicional, enlaces, referencias, etc..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>

          {/* Información del Usuario */}
          <div className="bg-gray-50 rounded-lg p-3">
            <p className="text-sm text-gray-600">
              <strong>Registrado por:</strong> {currentUser?.name || currentUser?.email || 'Sistema'}
            </p>
            <p className="text-sm text-gray-600">
              <strong>Fecha:</strong> {new Date().toLocaleString('es-ES')}
            </p>
          </div>

          {/* Botones */}
          <div className="flex justify-end space-x-4 pt-6 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !selectedAction || !description.trim()}
              className="px-6 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Guardando...' : 'Registrar Acción'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CpcTrafficClientsQuickActionModal;
